/**
 * Reports - read-first, shareable, market-segmentable posture for the period.
 *
 * Replaces the cold-start Report Builder: a written headline + posture stats
 * up top (from `/summary`, including the DQ-vs-Silent resolution split), depth
 * tabs that change density only (Summary / Detail / Full), and an Excel export
 * that reuses the existing async report job (the full FP x type grid + period
 * comparison) regardless of on-screen depth.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

import DataFetchWrapper from '../../../../components/inspector/shared/DataFetchWrapper'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import { useDataExceptionReport } from '../../../../hooks/useDataExceptionReport'
import {
  dataExceptionsApi,
  type ExceptionSummary,
} from '../../../../services/admin/dataExceptionsService'
import { poolwerxColors } from '../../../../theme/colors'
import { useDataExceptionsCtx } from '../DataExceptionsContext'
import FpGrid from './FpGrid'

type Depth = 'summary' | 'detail' | 'full'

const StatTile: React.FC<{ label: string; value: React.ReactNode; caption?: string }> = ({
  label,
  value,
  caption,
}) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: poolwerxColors.teal }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
)

const Reports: React.FC = () => {
  const { market, refreshKey } = useDataExceptionsCtx()
  const [depth, setDepth] = useState<Depth>('summary')
  const { job, isPolling, submit } = useDataExceptionReport()

  const { data: summary, loading, error } = useDataFetching<ExceptionSummary>({
    fetchFn: () => dataExceptionsApi.getSummary({ market: market || undefined }),
    dependencies: [market, refreshKey],
  })

  const downloadUrl =
    job?.status === 'completed' ? dataExceptionsApi.reportDownloadUrl(job.job_id) : null

  // Auto-open the file once the export job finishes (a download anchor, not a
  // route navigation).
  useEffect(() => {
    if (downloadUrl) {
      const a = document.createElement('a')
      a.href = downloadUrl
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
  }, [downloadUrl])

  const headline = useMemo(() => {
    if (!summary) return ''
    const open = summary.needs_review + summary.blocked
    const delta = summary.resolved_last_30d - summary.prior_period_resolved
    const dir = delta === 0 ? 'level with' : delta > 0 ? `up ${delta} on` : `down ${Math.abs(delta)} on`
    const scope = market ? `${market}` : 'the platform'
    return (
      `Across ${scope}, the team cleared ${summary.resolved_last_30d.toLocaleString()} exceptions in the ` +
      `last 30 days (${dir} the prior period), with ${open.toLocaleString()} still open ` +
      `(${summary.blocked.toLocaleString()} blocked).`
    )
  }, [summary, market])

  const onExport = () => {
    submit({
      market: market || undefined,
      compare: true,
      compare_unit: 'week',
      compare_periods: 4,
    })
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Tabs value={depth} onChange={(_e, v) => setDepth(v)}>
          <Tab value="summary" label="Summary" />
          <Tab value="detail" label="Detail" />
          <Tab value="full" label="Full" />
        </Tabs>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={onExport}
          disabled={isPolling}
        >
          {isPolling ? 'Generating...' : 'Export Excel'}
        </Button>
      </Box>

      {job?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {job.error_message || 'Export failed.'}
        </Alert>
      )}

      <DataFetchWrapper loading={loading} error={error} data={summary}>
        {summary && (
          <Stack spacing={3}>
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              {headline}
            </Typography>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <StatTile
                label="Resolved (30d)"
                value={summary.resolved_last_30d.toLocaleString()}
                caption={
                  `${summary.resolution_attribution.after_evo_action.toLocaleString()} after Evo action · ` +
                  `${summary.resolution_attribution.silent.toLocaleString()} silently`
                }
              />
              <StatTile
                label="Open backlog"
                value={(summary.needs_review + summary.blocked).toLocaleString()}
                caption={`${summary.aging_30d_plus.toLocaleString()} aging 30d+`}
              />
              <StatTile
                label="Avg to resolve"
                value={summary.avg_resolve_hours != null ? `${summary.avg_resolve_hours}h` : '-'}
                caption="from detection to sync-confirmed"
              />
              <StatTile
                label="In discussion"
                value={summary.in_discussion.toLocaleString()}
                caption="open rows with notes"
              />
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Resolution attribution is forward-only - exceptions resolved before this
              tooling shipped all count as Silent.
            </Typography>

            {(depth === 'detail' || depth === 'full') && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Open by domain
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell align="right">Open</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.by_domain.map((d) => (
                        <TableRow key={d.data_domain}>
                          <TableCell>{d.data_domain}</TableCell>
                          <TableCell align="right">{d.open.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {depth === 'full' && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  FP Grid snapshot
                </Typography>
                <FpGrid />
              </Box>
            )}
          </Stack>
        )}
      </DataFetchWrapper>
    </Box>
  )
}

export default Reports
